import { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
// useState still used by MobileSidebar
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'
import { useSession, authClient } from '@/client/lib/auth'
import { LayoutDashboard, Bug, Globe, Link2, Menu, Moon, Sun, Monitor, LogOut, Plus, Bell, TrendingDown, AlertTriangle, X, FileText, Settings } from 'lucide-react'
import { useTheme } from '@/client/components/theme-provider'
import { cn } from '@/lib/utils'
import { useSiteAlerts, useAcknowledgeAlert, type SiteAlert } from '@/client/modules/site/hooks/useSites'

type NavItem = {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Crawls', href: '/dashboard/crawls', icon: Bug },
  { label: 'Sites', href: '/dashboard/sites', icon: Globe },
]

const integrationNavItems: NavItem[] = [
  { label: 'Accounts', href: '/dashboard/integrations', icon: Link2 },
  { label: 'Alert Settings', href: '/dashboard/alert-settings', icon: Settings },
]

function isActive(href: string, pathname: string): boolean {
  if (href === '/dashboard') {
    return pathname === '/dashboard'
  }
  return pathname.startsWith(href)
}

function NavLink({
  item,
  pathname,
  onClick,
}: {
  item: NavItem
  pathname: string
  onClick?: () => void
}) {
  const Icon = item.icon
  const active = isActive(item.href, pathname)

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </Link>
  )
}

function SidebarNav({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
  return (
    <>
      <nav className="flex-1 space-y-1 p-4">
        {mainNavItems.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} onClick={onNavigate} />
        ))}

        <div className="pt-4 pb-1">
          <span className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
            Integrations
          </span>
        </div>
        {integrationNavItems.map((item) => (
          <NavLink key={item.href} item={item} pathname={pathname} onClick={onNavigate} />
        ))}
      </nav>

      <div className="border-t border-border p-4">
        <Link to="/dashboard/crawls/new" onClick={onNavigate}>
          <Button className="w-full" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            New Crawl
          </Button>
        </Link>
      </div>
    </>
  )
}

function Sidebar() {
  const location = useLocation()

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-56 border-r border-border bg-background">
      <div className="flex h-full flex-col">
        <div className="flex h-16 items-center border-b border-border px-6">
          <Link to="/dashboard" className="flex items-center gap-2 font-semibold text-lg">
            <span>Whispering Wombat</span>
          </Link>
        </div>
        <SidebarNav pathname={location.pathname} />
      </div>
    </aside>
  )
}

function MobileSidebar() {
  const location = useLocation()
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-56 p-0">
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center border-b border-border px-6">
            <Link to="/dashboard" className="font-semibold text-lg">
              Whispering Wombat
            </Link>
          </div>
          <SidebarNav pathname={location.pathname} onNavigate={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  )
}

function NotificationBell() {
  const { data: alertsData } = useSiteAlerts(false)
  const acknowledgeAlert = useAcknowledgeAlert()
  const alerts = alertsData?.alerts ?? []
  // Badge shows the TRUE total — not array length, which is capped by server
  // LIMIT (default 50). Without this, sidebar disagreed with Sites page COUNT(*).
  const totalCount = alertsData?.totalCount ?? alerts.length

  if (totalCount === 0) return null

  const alertTypeIcon = (type: string) => {
    switch (type) {
      case 'regression': return <TrendingDown className="h-3.5 w-3.5 text-destructive" />
      case 'crawl_failure': return <X className="h-3.5 w-3.5 text-destructive" />
      case 'keyword_drop': return <TrendingDown className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
      case 'backlink_loss': return <Link2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
      case 'page_change': return <FileText className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
      default: return <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" />
    }
  }

  const alertTypeLabel = (type: string) => {
    switch (type) {
      case 'regression': return 'Regression'
      case 'recovery': return 'Recovery'
      case 'crawl_failure': return 'Crawl Failed'
      case 'keyword_drop': return 'Ranking Drop'
      case 'backlink_loss': return 'Backlink Loss'
      case 'page_change': return 'Page Changes'
      default: return type
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] rounded-full px-1 flex items-center justify-center text-[10px] leading-none"
          >
            {totalCount > 99 ? '99+' : totalCount}
          </Badge>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b">
          <p className="text-sm font-medium">Alerts</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {alerts.slice(0, 10).map((alert: SiteAlert) => (
            <div key={alert.id} className="flex items-start gap-2 px-3 py-2.5 hover:bg-accent text-sm border-b border-border/50 last:border-0">
              <div className="mt-0.5 shrink-0">
                {alertTypeIcon(alert.alert_type)}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/dashboard/sites/${alert.site_id}`} className="font-medium hover:underline truncate block">
                  {alert.domain}
                </Link>
                <p className="text-xs text-muted-foreground">{alertTypeLabel(alert.alert_type)}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 shrink-0"
                onClick={() => acknowledgeAlert.mutate(alert.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
        {totalCount > 10 && (
          <div className="p-2 border-t text-center">
            <Link to="/dashboard" className="text-xs text-primary hover:underline">
              View all alerts
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function Header() {
  const { data: session } = useSession()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()

  const handleSignOut = async () => {
    await authClient.signOut()
    navigate('/sign-in')
  }

  const cycleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    setTheme(next)
  }

  const userInitials = session?.user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U'

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-background px-4 md:px-6">
      <MobileSidebar />
      <div className="flex-1" />

      <NotificationBell />

      <Button variant="ghost" size="icon" onClick={cycleTheme} title={`Theme: ${theme}`}>
        {theme === 'light' ? <Sun className="h-5 w-5" /> : theme === 'dark' ? <Moon className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar className="h-10 w-10">
              <AvatarImage src={session?.user?.image || undefined} alt={session?.user?.name || 'User'} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <p className="text-sm font-medium">{session?.user?.name}</p>
            <p className="text-xs text-muted-foreground">{session?.user?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}

export function DashboardLayout() {
  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className="flex flex-1 flex-col md:pl-56 min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-background p-4 md:p-6 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
